import React from 'react';
import { useTranslation } from 'react-i18next';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../styles';

interface TrialHeroProps {
  isTrialEligible: boolean;
  showDiscount?: boolean;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const TrialHero: React.FC<TrialHeroProps> = ({ isTrialEligible, showDiscount = false }) => {
  const { t } = useTranslation();
  const getHeadline = () => {
    if (!isTrialEligible) {
      return showDiscount ? t('subscription.unlockProDiscount') : t('subscription.unlockPro');
    }
    return showDiscount ? t('subscription.tryProFreeDiscount') : t('subscription.tryProFree');
  };

  const headline = getHeadline();

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: ${spacing.deci} 0 ${spacing.deci} 0;
        animation: ${fadeIn} 0.4s ease-out forwards;
      `}
    >
      {/* Onboarding reward acknowledgment */}
      {showDiscount && (
        <div
          css={css`
            ${fontStyles.cat};
            color: ${colors.tungsten};
            margin-bottom: ${spacing.centi};

            html[data-theme='dark'] & {
              color: ${colors.silver};
            }
          `}
        >
          {t('subscription.thanksForSettingUp')}
        </div>
      )}

      {/* Primary headline - this is what we're selling */}
      <h2
        css={css`
          ${fontStyles.shark};
          color: ${colors.liquorice};
          text-align: center;
          margin: 0 0 ${spacing.centi} 0;

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {headline}
      </h2>

      {/* Social proof - uses thinFlea (0.75rem) so it fits on one line at all font scales */}
      <div
        css={css`
          ${fontStyles.thinFlea};
          color: ${colors.tungsten};
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${spacing.centi};
          flex-wrap: wrap;

          html[data-theme='dark'] & {
            color: ${colors.silver};
          }
        `}
      >
        <span>{t('subscription.appStoreRating')}</span>
        <span
          css={css`
            opacity: 0.4;
          `}
        >
          ·
        </span>
        <span>{t('subscription.downloads')}</span>
        <span
          css={css`
            opacity: 0.4;
          `}
        >
          ·
        </span>
        <span>{t('subscription.nycBased')}</span>
      </div>
    </div>
  );
};
